"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { ImageIcon, Loader2, Upload, X } from "lucide-react";
import * as React from "react";
import type { UploadHandler } from "../lib/types";
import { cn, formatBytes, isImageFile } from "../lib/utils";

const containerVariants = cva(
  "relative rounded-lg overflow-hidden bg-muted border-2 border-dashed border-muted-foreground/25 flex items-center justify-center cursor-pointer transition-all group hover:border-muted-foreground/50",
  {
    variants: {
      size: {
        sm: "h-16",
        default: "h-24",
        lg: "h-32",
        xl: "h-40",
      },
      aspect: {
        square: "aspect-square",
        wide: "aspect-video",
        banner: "aspect-[3/1]",
        auto: "",
      },
    },
    defaultVariants: {
      size: "default",
      aspect: "wide",
    },
  },
);

export interface LogoUploadProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    VariantProps<typeof containerVariants> {
  /**
   * Upload handler configuration
   */
  handler: UploadHandler;
  /**
   * Current logo URL
   */
  value?: string | null;
  /**
   * Called when logo changes
   */
  onChange?: (url: string | null) => void;
  /**
   * Called when upload completes
   */
  onUploadComplete?: (url: string) => void;
  /**
   * Maximum file size in bytes
   * @default 5 * 1024 * 1024 (5MB)
   */
  maxSize?: number;
  /**
   * Accepted image types
   * @default ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"]
   */
  accept?: string[];
  /**
   * Whether to allow removing the logo
   * @default true
   */
  allowRemove?: boolean;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Placeholder text
   * @default "Upload logo"
   */
  placeholder?: string;
  /**
   * Object fit for the image
   * @default "contain"
   */
  objectFit?: "contain" | "cover" | "fill";
}

type UploadState = "idle" | "uploading" | "processing" | "error";

export function LogoUpload({
  handler,
  value,
  onChange,
  onUploadComplete,
  maxSize = 5 * 1024 * 1024,
  accept = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"],
  allowRemove = true,
  disabled = false,
  placeholder = "Upload logo",
  objectFit = "contain",
  size,
  aspect,
  className,
  ...props
}: LogoUploadProps) {
  const [uploadState, setUploadState] = React.useState<UploadState>("idle");
  const [error, setError] = React.useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Clean up preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleClick = React.useCallback(() => {
    if (!disabled && uploadState === "idle") {
      inputRef.current?.click();
    }
  }, [disabled, uploadState]);

  const handleRemove = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      onChange?.(null);
    },
    [onChange, previewUrl],
  );

  const handleFileSelect = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";

      if (!file) return;

      // Validate file type
      const isValidType = isImageFile(file.type) || file.type === "image/svg+xml" || accept.includes(file.type);

      if (!isValidType) {
        setError("Please select a valid image file");
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        setError(`Image must be less than ${formatBytes(maxSize)}`);
        return;
      }

      setError(null);

      // Create preview
      const objectUrl = URL.createObjectURL(file);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(objectUrl);

      try {
        setUploadState("uploading");

        // Get upload URL
        const { uploadUrl, assetId } = await handler.getUploadUrl(file);

        // Upload file
        const response = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!response.ok) {
          throw new Error("Upload failed");
        }

        setUploadState("processing");

        // Confirm upload
        let finalUrl = objectUrl;
        if (handler.onUploadComplete && assetId) {
          const result = await handler.onUploadComplete(assetId, file);
          if (result.url) {
            finalUrl = result.url;
            URL.revokeObjectURL(objectUrl);
            setPreviewUrl(null);
          }
        }

        setUploadState("idle");
        onChange?.(finalUrl);
        onUploadComplete?.(finalUrl);
      } catch (err) {
        setUploadState("error");
        setError(err instanceof Error ? err.message : "Upload failed");
        handler.onError?.(err instanceof Error ? err : new Error("Upload failed"), file);
      }
    },
    [accept, handler, maxSize, onChange, onUploadComplete, previewUrl],
  );

  const displayUrl = previewUrl || value;
  const isLoading = uploadState === "uploading" || uploadState === "processing";

  return (
    <div className={cn("w-full", className)} {...props}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleClick();
          }
        }}
        className={cn(
          containerVariants({ size, aspect }),
          displayUrl && "border-solid border-muted-foreground/15",
          disabled && "opacity-50 cursor-not-allowed",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept.join(",")}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || isLoading}
        />

        {/* Logo image or placeholder */}
        {displayUrl ? (
          <>
            <img
              src={displayUrl}
              alt="Logo"
              className={cn(
                "h-full w-full p-2",
                objectFit === "contain" && "object-contain",
                objectFit === "cover" && "object-cover",
                objectFit === "fill" && "object-fill",
              )}
            />
            {/* Hover overlay */}
            {!disabled && !isLoading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Upload className="h-6 w-6 text-white" />
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 p-4">
            <ImageIcon className="h-8 w-8 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{placeholder}</span>
          </div>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          </div>
        )}

        {/* Remove button */}
        {allowRemove && displayUrl && !disabled && !isLoading && (
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 transition-colors opacity-0 group-hover:opacity-100"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Error message */}
      {error && <p className="text-xs text-destructive mt-2">{error}</p>}

      {/* Status message */}
      {isLoading && (
        <p className="text-xs text-muted-foreground mt-2">
          {uploadState === "uploading" ? "Uploading..." : "Processing..."}
        </p>
      )}
    </div>
  );
}

LogoUpload.displayName = "LogoUpload";
